import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';
import { ArrowRight, Clock } from 'lucide-react';
import blogPosts from '@/data/blogPosts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Blog | CoinPayUp';
    axios.get(`${API}/blogs/public`).then(({ data }) => {
      // Merge API data with static content for rich display
      const merged = data.map(b => {
        const staticMatch = blogPosts.find(s => s.slug === b.slug);
        return { ...b, hasContent: !!(b.content || staticMatch) };
      });
      setBlogs(merged);
    }).catch(() => {
      // Fallback to static data
      setBlogs(blogPosts.map(b => ({ ...b, id: b.slug, hasContent: true })));
    }).finally(() => setLoading(false));
  }, []);

  // JSON-LD: Blog (CollectionPage) — injected separately once blogs are loaded
  useEffect(() => {
    if (!blogs.length) return;
    const blogSchema = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "CoinPayUp Blog",
      "description": "Insights on crypto payments, payment security, and building the future of digital commerce.",
      "url": "https://coinpayup.com/blogs",
      "publisher": { "@type": "Organization", "name": "CoinPayUp", "url": "https://coinpayup.com" },
      "blogPost": blogs.slice(0, 10).map(b => ({
        "@type": "BlogPosting",
        "headline": b.title,
        "description": b.excerpt || '',
        "url": `https://coinpayup.com/blogs/${b.slug}`,
        "datePublished": b.created_at ? b.created_at.substring(0, 10) : '',
        "dateModified": b.updated_at ? b.updated_at.substring(0, 10) : '',
        "author": { "@type": "Organization", "name": "CoinPayUp" }
      }))
    };
    const ldEl = document.createElement('script');
    ldEl.type = 'application/ld+json';
    ldEl.setAttribute('data-schema', 'blogs-list');
    ldEl.text = JSON.stringify(blogSchema);
    document.head.appendChild(ldEl);
    return () => ldEl.remove();
  }, [blogs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="blogs-page">
        <SiteHeader />
        <div className="max-w-6xl mx-auto px-6 py-24 text-center text-muted-foreground text-sm">Loading...</div>
        <SiteFooter />
      </div>
    );
  }

  const featured = blogs[0];
  const rest = blogs.slice(1);

  return (
    <div className="min-h-screen bg-background" data-testid="blogs-page">
      <SiteHeader />

      {/* Header */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>Blog</h1>
          <p className="text-base md:text-lg text-muted-foreground mt-3 max-w-xl">
            Insights on crypto payments, payment security, and building the future of digital commerce.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {blogs.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No blog posts published yet.</div>
        ) : (
          <>
            {/* Featured Post */}
            {featured && (
              <Link to={`/blogs/${featured.slug}`} className="group block mb-12" data-testid="featured-post">
                <div className={`rounded-2xl border border-border overflow-hidden transition-all group-hover:border-primary/40 group-hover:shadow-lg group-hover:shadow-primary/5`}>
                  {featured.cover_image ? (
                    <div className="relative h-56 overflow-hidden">
                      <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover transition-transform group-hover:scale-[1.02]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                    </div>
                  ) : null}
                  <div className={`p-8 md:p-12 ${featured.cover_image ? '' : `bg-gradient-to-br ${featured.cover_gradient || featured.coverGradient || 'from-indigo-600/20 to-cyan-600/20'}`}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">{featured.category}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{featured.read_time || featured.readTime}</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{featured.title}</h2>
                    <p className="text-muted-foreground text-base leading-relaxed max-w-2xl mb-6">{featured.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2.5 transition-all">Read article <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </div>
              </Link>
            )}

            {/* Post Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-grid">
              {rest.map(post => (
                <Link key={post.slug} to={`/blogs/${post.slug}`} className="group" data-testid={`blog-card-${post.slug}`}>
                  <article className="rounded-xl border border-border overflow-hidden bg-card hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all h-full flex flex-col">
                    {post.cover_image ? (
                      <div className="h-40 overflow-hidden">
                        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]" />
                      </div>
                    ) : (
                      <div className={`h-32 bg-gradient-to-br ${post.cover_gradient || post.coverGradient || 'from-indigo-600/20 to-cyan-600/20'}`} />
                    )}
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-muted text-muted-foreground">{post.category}</span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{post.read_time || post.readTime}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2 leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{post.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed flex-1">{post.excerpt}</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary mt-4 group-hover:gap-2.5 transition-all">Read more <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
};

export default BlogsPage;
